import { useState, useEffect } from "react";
import { Clock, RefreshCw, AlertTriangle, ChevronRight, Shield } from "lucide-react";
import api from "../../services/api";

function fmtCurrency(v) {
  if (!v) return "R$ 0";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

var TYPE_LABELS = { auto: "Auto", residencial: "Residencial", vida: "Vida", empresarial: "Empresarial" };

function getUrgencyCls(days) {
  if (days <= 7) return "border-red-200 bg-red-50";
  if (days <= 30) return "border-amber-200 bg-amber-50";
  return "border-blue-200 bg-blue-50";
}

function getBadgeCls(days) {
  if (days <= 7) return "bg-red-100 text-red-700";
  if (days <= 30) return "bg-amber-100 text-amber-700";
  return "bg-blue-100 text-blue-700";
}

function RenewalCard({ policy, onRenew }) {
  var urgCls = getUrgencyCls(policy.days_left);
  var badgeCls = getBadgeCls(policy.days_left);
  var typeLbl = TYPE_LABELS[policy.insurance_type] || policy.insurance_type;

  return (
    <div className={"rounded-xl border p-3 sm:p-4 " + urgCls} data-testid={"renewal-card-" + policy.id}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="text-sm font-semibold text-[#0A2F3D] truncate">{policy.client_name}</p>
            <span className={"px-1.5 py-0.5 rounded text-[10px] font-medium " + badgeCls}>{policy.days_left}d</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="font-mono">{policy.policy_number}</span>
            <span className="hidden sm:inline">|</span>
            <span>{policy.insurer_name}</span>
            <span className="hidden sm:inline">|</span>
            <span className="capitalize">{typeLbl}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
            <span>Vence: <span className="font-medium text-gray-700">{policy.end_date}</span></span>
            <span>Premio: <span className="font-medium text-emerald-600">{fmtCurrency(policy.premium)}</span></span>
          </div>
        </div>
        <button onClick={function() { onRenew(policy); }} className="flex items-center gap-1.5 px-3 py-2 bg-[#3B82F6] text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors flex-shrink-0 self-start sm:self-center" data-testid={"renew-btn-" + policy.id}>
          <RefreshCw className="w-3.5 h-3.5" />Iniciar Cotacao
        </button>
      </div>
    </div>
  );
}

export default function RenewalPanel({ onStartQuote }) {
  var policiesState = useState([]);
  var policies = policiesState[0];
  var setPolicies = policiesState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var daysState = useState(60);
  var days = daysState[0];
  var setDays = daysState[1];

  useEffect(function() {
    setLoading(true);
    api.get("/policies/expiring?days=" + days).then(function(res) {
      setPolicies(res.data.policies || []);
    }).finally(function() { setLoading(false); });
  }, [days]);

  function handleRenew(policy) {
    if (onStartQuote) {
      onStartQuote(policy);
    }
  }

  var criticalCount = policies.filter(function(p) { return p.days_left <= 7; }).length;
  var warningCount = policies.filter(function(p) { return p.days_left > 7 && p.days_left <= 30; }).length;

  return (
    <div data-testid="renewal-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-amber-50 rounded-xl"><Clock className="w-5 h-5 text-amber-500" /></div>
          <div>
            <h3 className="text-sm sm:text-base font-bold text-[#0A2F3D]">Renovacoes Pendentes</h3>
            <p className="text-xs text-gray-500">{policies.length} apolice(s) a vencer</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />{criticalCount} critica(s)</span>
          )}
          {warningCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium bg-amber-100 text-amber-700"><Clock className="w-3 h-3" />{warningCount} atencao</span>
          )}
          <select value={days} onChange={function(e) { setDays(parseInt(e.target.value)); }} className="px-2 py-1.5 border rounded-lg text-xs" data-testid="renewal-days-filter">
            <option value="30">30 dias</option>
            <option value="60">60 dias</option>
            <option value="90">90 dias</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : policies.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-100">
          <Shield className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
          <p className="text-sm text-gray-400">Nenhuma apolice a vencer nos proximos {days} dias</p>
        </div>
      ) : (
        <div className="space-y-2">
          {policies.map(function(p) {
            return <RenewalCard key={p.id} policy={p} onRenew={handleRenew} />;
          })}
        </div>
      )}
    </div>
  );
}
